import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import {
  GET_STORES, APPROVE_STORE, CHANGE_STORE_STATUS,
  type ManageStoresData, type ManagedStore,
} from '../../graphql/management'
import { StoreStatusBadge } from './StoreStatusBadge'
import { Feedback, useFormFeedback, type UserError } from './Feedback'

const STATUSES = ['', 'pending', 'active', 'paused', 'suspended', 'closed']

// Mall-owner store management: list + status filter + can_*-gated actions.
// Mirrors iOS StoreManagementView. Refetch-based for correctness.
export default function StoresList() {
  const fb = useFormFeedback()
  const [status, setStatus] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const { data, loading, refetch } = useQuery<ManageStoresData>(GET_STORES, {
    variables: { status: status || null }, fetchPolicy: 'cache-and-network',
  })
  const [approve] = useMutation(APPROVE_STORE)
  const [changeStatus] = useMutation(CHANGE_STORE_STATUS)

  async function run(id: string, action: () => Promise<UserError[] | undefined>) {
    setBusyId(id); fb.reset()
    try {
      const errs = await action()
      if (!fb.fromUserErrors(errs)) return
      await refetch()
    } catch (err) {
      fb.setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyId(null)
    }
  }

  const onApprove = (s: ManagedStore) => run(s.id, async () => {
    const { data } = await approve({ variables: { id: s.id } })
    return data?.approveStore?.userErrors
  })
  const onStatus = (s: ManagedStore, next: string) => run(s.id, async () => {
    const { data } = await changeStatus({ variables: { id: s.id, status: next } })
    return data?.changeStoreStatus?.userErrors
  })

  const stores = data?.stores ?? []
  return (
    <div className="manage-page">
      <header className="manage-header">
        <h1>Stores</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s ? s[0].toUpperCase() + s.slice(1) : 'All statuses'}</option>)}
          </select>
          <Link className="btn-primary" to="/manage/stores/new">New store</Link>
        </div>
      </header>

      <Feedback error={fb.error} success={fb.success} />

      {loading && !data ? (
        <div className="muted">Loading stores…</div>
      ) : stores.length === 0 ? (
        <div className="empty-state"><h2>No stores</h2><p className="muted">No stores match this filter.</p></div>
      ) : (
        <ul className="store-list">
          {stores.map((s) => {
            const busy = busyId === s.id
            return (
              <li key={s.id} className="store-card">
                <div className="store-card-main">
                  <div className="store-card-head">
                    <strong>{s.name}</strong>
                    <StoreStatusBadge status={s.status} />
                  </div>
                  {s.description && <p className="muted small">{s.description}</p>}
                  <p className="muted small">{s.productsCount} products · {s.user.fullName || s.user.email}</p>
                </div>
                <div className="store-card-actions">
                  {s.canApprove && <button className="btn-primary" disabled={busy} onClick={() => onApprove(s)}>Approve</button>}
                  {s.canActivate && <button className="btn-primary" disabled={busy} onClick={() => onStatus(s, 'active')}>Resume</button>}
                  {s.canPause && <button className="btn-secondary" disabled={busy} onClick={() => onStatus(s, 'paused')}>Pause</button>}
                  {s.canSuspend && <button className="btn-danger" disabled={busy} onClick={() => onStatus(s, 'suspended')}>Suspend</button>}
                  {s.canEdit && <Link className="btn-link" to={`/manage/stores/${s.id}/edit`}>Edit</Link>}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
